import { useState } from "react";
import LegalPage from "@/components/LegalPage";
import { Mail, Globe, Loader2, Send, Check, Clock, MessageSquare } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const REQUEST_TYPES = [
  { value: "genel", label: "Genel bilgi" },
  { value: "paket", label: "Paketler hakkında bilgi" },
  { value: "destek", label: "Teknik destek" },
  { value: "satis", label: "Satış / demo talebi" },
  { value: "isbirligi", label: "İş birliği" },
  { value: "diger", label: "Diğer" },
];

export default function Contact() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    business_name: "",
    website: "",
    request_type: "genel",
    subject: "",
    message: "",
    // Honeypot — must remain empty
    company_website_hidden: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    const fullName = form.full_name.trim();
    const subject = form.subject.trim();
    const message = form.message.trim();
    if (!fullName) return toast.error("Ad Soyad boş olamaz.");
    if (!subject) return toast.error("Konu boş olamaz.");
    if (message.length < 10) return toast.error("Mesaj en az 10 karakter olmalı.");

    setLoading(true);
    try {
      const { data } = await api.post("/contact-requests", form);
      toast.success(data?.message || "Mesajınız alındı.");
      setSent(true);
      setForm({
        full_name: "", email: "", phone: "", business_name: "", website: "",
        request_type: "genel", subject: "", message: "", company_website_hidden: "",
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (!err?.response) {
        toast.error("Mesaj gönderilemedi. Lütfen birkaç dakika sonra tekrar deneyin.");
      } else {
        toast.error(detail || "Mesaj gönderilemedi. Lütfen birkaç dakika sonra tekrar deneyin.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <LegalPage
      title="Bizimle İletişime Geçin"
      subtitle="uSeen hakkında soru sormak, paketler hakkında bilgi almak veya işletmeniz için AI görünürlük sürecini konuşmak için bize ulaşın."
      testid="contact-page"
    >
      <div className="grid gap-8 md:grid-cols-2 not-prose">
        {/* Sol kolon */}
        <div className="space-y-4">
          <div className="rounded-2xl glass-strong border border-white/5 p-5">
            <div className="flex items-center gap-3 mb-2">
              <Globe size={16} className="text-cyan-300" />
              <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400">Web</div>
            </div>
            <div className="text-white text-sm">useen.ai</div>
          </div>
          <div className="rounded-2xl glass-strong border border-white/5 p-5">
            <div className="flex items-center gap-3 mb-2">
              <Mail size={16} className="text-cyan-300" />
              <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400">E-posta</div>
            </div>
            <a href="mailto:contact@useen.ai" className="text-white text-sm hover:text-cyan-300" data-testid="contact-info-email">contact@useen.ai</a>
          </div>
          <div className="rounded-2xl bg-cyan-500/5 border border-cyan-500/20 p-5">
            <div className="flex items-center gap-3 mb-2">
              <Clock size={16} className="text-cyan-300" />
              <div className="text-[11px] uppercase tracking-[0.18em] text-cyan-200">Yanıt Süresi</div>
            </div>
            <div className="text-sm text-slate-200 leading-relaxed">
              Genellikle <strong className="text-white">1 iş günü</strong> içinde dönüş yaparız. KVKK kapsamındaki taleplere yasal süre olan 30 gün içinde yanıt veriyoruz.
            </div>
          </div>
          <div className="text-xs text-slate-500 leading-relaxed px-1">
            Paket talepleriniz için <a href="/fiyatlandirma" className="text-cyan-300 hover:text-cyan-200">Planlar</a> sayfasından da başvuru oluşturabilirsiniz.
          </div>
        </div>

        {/* Sağ kolon — form */}
        <div className="rounded-2xl glass-strong border border-white/10 p-6">
          <div className="flex items-center gap-2 mb-1">
            <MessageSquare size={16} className="text-cyan-300" />
            <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400">İletişim Formu</div>
          </div>
          <h3 className="font-display text-xl font-semibold text-white mb-5">Mesajınızı bırakın</h3>

          {sent ? (
            <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/20 p-5 text-sm text-slate-200 leading-relaxed" data-testid="contact-sent-card">
              <div className="flex items-center gap-2 mb-2">
                <Check size={16} className="text-emerald-300" />
                <strong className="text-white">Mesajınız alındı.</strong>
              </div>
              uSeen ekibi en kısa sürede sizinle iletişime geçecek.
              <button
                type="button"
                onClick={() => setSent(false)}
                className="mt-4 text-xs text-cyan-300 hover:text-cyan-200"
                data-testid="contact-send-another"
              >
                Yeni mesaj gönder
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-3" data-testid="contact-form">
              <Field label="Ad Soyad *">
                <input type="text" required value={form.full_name} onChange={set("full_name")} className="cinput" placeholder="Adınız Soyadınız" data-testid="contact-full-name" />
              </Field>
              <Field label="E-posta *">
                <input type="email" required value={form.email} onChange={set("email")} className="cinput" placeholder="ornek@isletmem.com" data-testid="contact-email" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Telefon">
                  <input type="tel" value={form.phone} onChange={set("phone")} className="cinput" placeholder="opsiyonel" data-testid="contact-phone" />
                </Field>
                <Field label="İşletme Adı">
                  <input type="text" value={form.business_name} onChange={set("business_name")} className="cinput" placeholder="opsiyonel" data-testid="contact-business-name" />
                </Field>
              </div>
              <Field label="Web Sitesi">
                <input type="url" value={form.website} onChange={set("website")} className="cinput" placeholder="https://... (opsiyonel)" data-testid="contact-website" />
              </Field>
              <Field label="Talep Türü">
                <select value={form.request_type} onChange={set("request_type")} className="cinput" data-testid="contact-request-type">
                  {REQUEST_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </Field>
              <Field label="Konu *">
                <input type="text" required value={form.subject} onChange={set("subject")} className="cinput" placeholder="Mesajınızın konusu" data-testid="contact-subject" />
              </Field>
              <Field label="Mesaj *">
                <textarea required rows={5} value={form.message} onChange={set("message")} className="cinput resize-none" placeholder="En az 10 karakter" data-testid="contact-message" />
              </Field>
              {/* Honeypot — hidden from real users but visible to bots */}
              <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: 1, height: 1, overflow: "hidden" }}>
                <label>Şirket web sitesi (boş bırakın)</label>
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.company_website_hidden}
                  onChange={set("company_website_hidden")}
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="neon-btn w-full text-white font-semibold rounded-xl py-3 flex items-center justify-center gap-2 disabled:opacity-60 mt-2"
                data-testid="contact-submit"
              >
                {loading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                Mesaj Gönder
              </button>
            </form>
          )}
        </div>
      </div>

      <style>{`
        .cinput {
          margin-top: 0.4rem;
          width: 100%;
          background: rgba(0,0,0,0.4);
          border: 1px solid rgba(255,255,255,0.10);
          border-radius: 0.5rem;
          padding: 0.65rem 0.85rem;
          color: #fff;
          font-size: 0.875rem;
        }
        .cinput:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 1px #3b82f6; }
      `}</style>
    </LegalPage>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[11px] text-slate-400 uppercase tracking-wider">{label}</label>
      {children}
    </div>
  );
}
